'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import { LoadingMaskRootContext } from './context/loading-mask-root.context';
import { loadingMaskRootStyles } from './styles/loading-mask-root.styles';
import type { LoadingMaskPropsInterface } from './types/loading-mask.types';

export const LoadingMaskRoot = forwardRef<HTMLDivElement, LoadingMaskPropsInterface>(
  (props: LoadingMaskPropsInterface, forwardedRef) => {
    const {
      children,
      className,
      css: cssProp,
      iconSize = 'lg',
      loading = true,
      loadingVoiceOver = 'Loading',
      onImpact = false,
      ...rest
    } = props;

    const styles = css(loadingMaskRootStyles, cssProp);

    const contextValue = useMemo(
      () => ({ loading, iconSize, onImpact }),
      [loading, iconSize, onImpact],
    );

    return (
      <LoadingMaskRootContext.Provider value={contextValue}>
        <div
          className={cx('c11n', 'c11n_loading-mask__root', styles, className)}
          data-c11n-component="LoadingMask.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          {...rest}
        >
          {/*
           * `role="status"` belongs here, not on the wrapper. On the wrapper it made the whole
           * masked subtree an atomic live region, so every mutation inside a mask re-read all
           * of it [CDS-6392].
           *
           * Stays mounted with its text toggled — a live region mounted at the same time as
           * its text is often missed by screen readers.
           */}
          <VisuallyHidden role="status">{loading ? loadingVoiceOver : ''}</VisuallyHidden>
          {children}
        </div>
      </LoadingMaskRootContext.Provider>
    );
  },
);

LoadingMaskRoot.displayName = 'LoadingMask.Root';
